import {
  CURRENT_ENTRY_DATA,
  ERROR_DATA,
  MONTHLY_REVIEW_DATA,
  TEST_VALUE_DATA,
  YEARLY_REVIEW_DATA,
  INIT_USER_DATA,
} from "./data"
import { resolvers, typeDefs } from "./resolvers"

import {
  ApolloClient,
  ApolloLink,
  HttpLink,
  gql,
  makeVar,
} from "@apollo/client"
import { RetryLink } from "@apollo/client/link/retry"
import { onError } from "@apollo/client/link/error"
import { setContext } from "@apollo/client/link/context"

// import ApolloClient from "apollo-client"
import { ApolloLink as _ApolloLink } from "apollo-link"
import { HttpLink as _HttpLink } from "apollo-link-http"
import { RetryLink as _RetryLink } from "apollo-link-retry"
import { onError as _onError } from "apollo-link-error"
import { setContext as _setContext } from "apollo-link-context"

import { Cashew } from "../refactored-functions/helpers/graphql"
import { cacheConfig } from "./cache"
import fetch from "isomorphic-fetch"
import { generateAuthenticationToken } from "../refactored-functions/auth.js"
import { errorer } from "../refactored-components/Dialogs/modals"
import { _FORCE_UPDATE } from "./queries"

let currentEnv = "development",
  serverEndpoint = process.env.GATSBY_GRAPHQL_ENDPOINT,
  defaultClientData

if (typeof window !== "undefined") {
  defaultClientData = {
    data: {
      beaconOpen: false,
      currentEntry: CURRENT_ENTRY_DATA,
      currentRoute: "",
      doesNotExist: false,
      error: ERROR_DATA,
      monthlyReview: MONTHLY_REVIEW_DATA, // makeVar(MONTHLY_REVIEW_DATA),
      selectedUpgradePlan: "plan-monthly", //should be set to default UI selection
      sidebarOpen: false,
      testValue: TEST_VALUE_DATA,
      userData: INIT_USER_DATA,
      userPlan: "plan-free",
      yearlyReview: YEARLY_REVIEW_DATA,
      forceUpdate: makeVar(false),
    },
  }

  // Write initial data to cache
  console.warn("Default Client Data", defaultClientData)
  cacheConfig.writeData(defaultClientData)

  const { forceUpdate } = cacheConfig.readQuery({
    query: _FORCE_UPDATE,
  })
  window._forceUpdate = forceUpdate

  // Set environment
  if (window.location.hostname.includes("reflection.app"))
    currentEnv = "production"
  if (
    window.location.hostname.includes("netlify.app") ||
    window.location.hostname.includes("ra-staging")
  )
    currentEnv = "staging"

  if (currentEnv !== "development") {
    serverEndpoint =
      currentEnv === "production"
        ? process.env.GATSBY_GRAPHQL_ENDPOINT_LIVE
        : currentEnv === "staging"
        ? process.env.GATSBY_GRAPHQL_ENDPOINT_STAGING
        : process.env.GATSBY_GRAPHQL_ENDPOINT
  }

  console.warn("LOG: Set current endpoint to:", serverEndpoint)
}

const httpLink = new HttpLink({
  uri: serverEndpoint,
  fetch,
})

const authLink = setContext(async (metadata, { headers }) => {
  let token
  // console.log(`LOG: current time is ${new Date().getTime()} and pt_time is ${window.localStorage.getItem('_pt_time') || 0} and diff is: ${new Date().getTime() - (window.localStorage.getItem('_pt_time') || 0) > 30000}`)
  try {
    if (
      new Date().getTime() - (window.localStorage.getItem("_pt_time") || 0) >
      3000
    ) {
      console.log("LOG: [Apollo Client] Retrieving fresh token")
      token = await generateAuthenticationToken(
        window.localStorage.getItem("_pt")
      )
      window.localStorage.setItem("_pt", token)
      window.localStorage.setItem("_pt_time", new Date().getTime())
      console.log("LOG: [Apollo Client] Operation Context:", {
        operation: metadata.operationName,
        token,
        metadata,
      })
    } else {
      token = window.localStorage.getItem("_pt")
      console.log("LOG: [Apollo Client] Retrieving cached token", { token })
    }
  } catch (error) {
    console.warn("TCL: AuthLink setContext failed to set token", error)
  }
  return {
    headers: {
      ...headers,
      authorization: `Bearer ${token}`,
    },
  }
})

const retryLink = new RetryLink()

const errorLink = onError(({ networkError, graphQLErrors }) => {
  let errorText, errorStack, consoleError
  // Handle graphQL errors
  if (graphQLErrors) {
    graphQLErrors.map(error => {
      const { message, path, extensions } = error

      errorText = `[Apollo GraphQL Error]: (${path}) ${message}`
      errorStack =
        extensions.exception && extensions.exception.stacktrace
          ? extensions.exception.stacktrace.join("\n")
          : "No stacktrace available."
      delete extensions.exception

      consoleError = `${errorText}\n\n${errorStack}`

      errorer({
        title: extensions.label || "Whoopsala!",
        code: extensions.code || "unexpected-error",
        ui: extensions.ui || "modal",
        message:
          extensions.display ||
          "There was an unexpected error.  Usually these are temporary, but if the issue persists, contact support.",
        internal: message,
      })

      console.log("Error metadata", extensions)
      console.error(consoleError)
    })
  }
  // Handle network errors
  if (networkError) {
    errorText = `[Apollo Network error]: ${networkError}`
    consoleError = `${errorText}`

    errorer({
      title: "Whoopsala!",
      code: "graphql-network-error",
      ui: "modal",
      message:
        "There was a network error which caused an unexpected error.  Usually these are temporary, but if the issue persists, contact support.  Please make a copy of any unsaved work or entries and try again in a moment.",
      internal: errorText,
    })

    console.error(consoleError)
  }
})

const getClientConfig = () => {
  return {
    cache: cacheConfig,
    typeDefs,
    resolvers,
    link: ApolloLink.from([errorLink, retryLink, authLink.concat(httpLink)]),
  }
}

/** @type {import("@apollo/client").ApolloClient<NormalizedCacheObject>} */
export const client = new ApolloClient(getClientConfig())
client.writeData = cacheConfig.writeData
client.cashew = new Cashew(client)

client.onClearStore(() => {
  console.warn("[Apollo Client] store has been cleared.")
  window.localStorage.removeItem("_pt_time")
  window.localStorage.removeItem("_pt")
  window.localStorage.removeItem("apollo-cache-persist")
  window.localStorage.removeItem("userDataLoaded")
  window.localStorage.removeItem("authToken")
  window.localStorage.removeItem("upvotyToken")
  window.localStorage.removeItem("validAuthToken")
  window.localStorage.removeItem("currentUser")
  window.localStorage.removeItem("cachedEntries")
  window.localStorage.removeItem("isPollingToken")
  window.localStorage.removeItem("isAuthRedirect")
  window.localStorage.removeItem("isAuthenticating")
  window.localStorage.removeItem("isIdentified")

  //Segment
  window.localStorage.removeItem("ajs_user_id")
  window.localStorage.removeItem("ajs_anonymous_id")
  window.localStorage.removeItem("ajs_group_id")
  window.localStorage.removeItem("ajs_user_traits")

  //TrackJS
  window.localStorage.removeItem("track_js_installed")
  window.localStorage.removeItem("events")
  window.TrackJS && (window.TrackJS = null)

  //Firebase
  window.authUser && (window.authUser = null)

  //Write default data to client
  cacheConfig.writeData(defaultClientData)
})

export default client
